import { Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { Nullable } from "../../../common/types/nullable";
import { IN_PROGRESS_TAB_INDEX } from "../inboxTabs";
import ForwardConversationDialog from "../ForwardConversationDialog";
import MessagingToolbar from "../MessagingToolbar";
import { getMinimizeToolbarAction } from "../messagingViewModeActions";
import ResolveConversationDialog from "../ResolveConversationDialog";
import { ParticipantOption } from "../types/common";
import useMessagingThread from "../useMessagingThread";
import useMessagingViewMode from "../useMessagingViewMode";
import { getPageToolbarActions } from "./conversationActions";
import ConversationAttachmentsList from "./ConversationAttachmentsList";
import ConversationDropZone from "./ConversationDropZone";
import ConversationParticipantHeader from "./ConversationParticipantHeader";
import ConversationThreadTabs, {
  ConversationThreadTab,
} from "./ConversationThreadTabs";
import Messages from "./Messages";
import MessagesContainer from "./MessagesContainer";
import MessagingComposer from "./MessagingComposer";

type Props = {
  participant: Nullable<ParticipantOption>;
  handleTabChange: (newValue: number, changeMode: "auto") => void;
};

const MessagingConversation = ({ participant, handleTabChange }: Props) => {
  const { t } = useTranslation("messaging");
  const { minimize } = useMessagingViewMode();
  const [forwardOpen, setForwardOpen] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [threadTab, setThreadTab] =
    useState<ConversationThreadTab>("conversation");

  const {
    messages,
    messagesLoading,
    messagesLoadingMore,
    loadMore,
    virtuosoRef,
    draft,
    onDraftChange,
    onSend,
    sendingDisabled,
    inputDisabled,
    composerPlaceholder,
    conversationTitle,
    titleLocked,
    onTitleChange,
    isCreatingConversation,
    isAssigning,
    canAssignConversation,
    isClosedConversation,
    canMarkAsDone,
    canTransfer,
    teamTransferDisabled,
    assignToMe,
    markAsDone,
    transfer,
    selectedConversationId,
    pendingFiles,
    addPendingFiles,
    removePendingFile,
  } = useMessagingThread({
    participant,
    onAssignedToMe: () => handleTabChange(IN_PROGRESS_TAB_INDEX, "auto"),
  });

  useEffect(() => {
    setResolveOpen(false);
    setForwardOpen(false);
    setThreadTab("conversation");
  }, [selectedConversationId]);

  return (
    <Grid
      item
      container
      xs
      direction="column"
      sx={{
        height: "100%",
        position: "relative",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <OverlaySpinner
        isLoading={isAssigning || isCreatingConversation}
        sx={{ position: "absolute" }}
      />
      <MessagingToolbar
        actions={[
          ...getPageToolbarActions({
            t,
            availability: {
              canAssignConversation,
              canMarkAsDone,
              canTransferConversation: canTransfer,
              isClosed: isClosedConversation,
            },
            handlers: {
              assignToMe,
              onForward: () => setForwardOpen(true),
              markAsDone: () => setResolveOpen(true),
            },
          }),
          getMinimizeToolbarAction({
            t,
            onClick: minimize,
            disabled: isCreatingConversation,
          }),
        ]}
        GridProps={{ item: true }}
        inputComponent={
          <ConversationParticipantHeader participant={participant} />
        }
      />
      <ConversationThreadTabs value={threadTab} onChange={setThreadTab} />
      <ConversationDropZone disabled={inputDisabled} onFiles={addPendingFiles}>
        <MessagesContainer disablePadding={threadTab === "attachments"}>
          {threadTab === "attachments" ? (
            <ConversationAttachmentsList messages={messages} />
          ) : (
            <>
              <Messages
                virtuosoRef={virtuosoRef}
                participant={participant}
                messages={messages}
                loadMore={loadMore}
                loading={messagesLoading}
                loadingMore={messagesLoadingMore}
              />
              <MessagingComposer
                value={draft}
                onChange={onDraftChange}
                onSend={onSend}
                sendingDisabled={sendingDisabled}
                inputDisabled={inputDisabled}
                placeholder={composerPlaceholder}
                title={conversationTitle}
                titleLocked={titleLocked}
                onTitleChange={onTitleChange}
                pendingFiles={pendingFiles}
                onAddFiles={addPendingFiles}
                onRemovePendingFile={removePendingFile}
              />
            </>
          )}
        </MessagesContainer>
      </ConversationDropZone>
      <ForwardConversationDialog
        open={forwardOpen}
        teamTransferDisabled={teamTransferDisabled}
        onClose={() => setForwardOpen(false)}
        onConfirm={(options) => {
          setForwardOpen(false);
          transfer(options);
        }}
      />
      <ResolveConversationDialog
        open={resolveOpen}
        onClose={() => setResolveOpen(false)}
        onConfirm={(options) => {
          setResolveOpen(false);
          markAsDone(options);
        }}
      />
    </Grid>
  );
};

export default MessagingConversation;
